import {
  GatewayChatService,
  type GatewayConfig,
} from "@/features/chat/services/gateway-chat-service";
import { buildCogoAppChatContext } from "@/features/chat/services/cogo-app-context";
import { buildStudioChatModelPolicy } from "@/features/chat/services/chat-model-policy";
import { resolveChatGatewayBase } from "@/features/chat/services/chat-gateway-config";
import { useStudioStore } from "@/store/studio-store";
import type { ChatContext } from "@/store/chat-types";

type CogoAppChatContextInput = Parameters<typeof buildCogoAppChatContext>[0];

export interface PreviewProposalChatInput {
  userText: string;
  selectedLabel: string;
  selectedElementType?: string;
  chatContext: ChatContext;
  currentProject: CogoAppChatContextInput;
}

export interface PreviewProposalChatResult {
  body: string;
  source: "qwen-gateway" | "local-fallback";
  mode: "text-replacement" | "requirement";
}

interface PreviewProposalChatDeps {
  service?: Pick<GatewayChatService, "sendMessage">;
  canUseGateway?: () => boolean;
}

function defaultGatewayConfig(): GatewayConfig {
  return {
    getGatewayUrl: () => resolveChatGatewayBase(),
    getJwt: () => useStudioStore.getState().session?.access_token,
    getProjectId: () => useStudioStore.getState().currentProjectId,
    getAnonKey: () => import.meta.env.VITE_SUPABASE_ANON_KEY || "",
  };
}

export function canUsePreviewProposalGateway(): boolean {
  const state = useStudioStore.getState();
  if (import.meta.env.VITE_E2E_MOCK === "true") return false;
  if (import.meta.env.VITE_CHAT_MOCK === "true") return false;
  if (state.forceMockChat) return false;
  if (!import.meta.env.VITE_SUPABASE_URL) return false;
  return Boolean(state.session?.access_token);
}

function buildPreviewProposalPrompt(input: PreviewProposalChatInput): string {
  return [
    "The user is refining what Coogo rendered.",
    "It may be an application, document, or report.",
    `Target: ${input.selectedLabel}`,
    input.selectedElementType
      ? `Selected element type: ${input.selectedElementType}`
      : "",
    `User request: ${input.userText}`,
    "",
    "Return one concise proposal sentence for what should change.",
    "If the target is text-like and the user asks for replacement copy, return only the exact replacement copy.",
    "If the request is about style, layout, data, or behavior, keep it as a short requirement.",
    "Do not output JSON, markdown, bullet lists, model names, or internal implementation terms.",
  ]
    .filter(Boolean)
    .join("\n");
}

function isTextLikeElementType(type: string | undefined): boolean {
  return !type || ["text", "heading", "label", "paragraph"].includes(type);
}

function explicitReplacementFromUserText(text: string): string | null {
  const quoted = text.match(/["“”'‘’]([^"“”'‘’]{1,160})["“”'‘’]/);
  const replacement = quoted?.[1]?.trim();
  return replacement || null;
}

function localFallbackProposal(
  input: PreviewProposalChatInput,
): PreviewProposalChatResult {
  const fallback = input.userText.trim();
  const explicitReplacement = isTextLikeElementType(input.selectedElementType)
    ? explicitReplacementFromUserText(fallback)
    : null;
  return {
    body: explicitReplacement ?? fallback,
    source: "local-fallback",
    mode: explicitReplacement ? "text-replacement" : "requirement",
  };
}

function normalizeProposalBody(
  text: string,
  fallback: string,
): { body: string; acceptedModelOutput: boolean } {
  const firstLine = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .find(Boolean);
  const proposal = firstLine?.replace(/^["'“”‘’]+|["'“”‘’]+$/g, "").trim();
  if (!proposal) return { body: fallback, acceptedModelOutput: false };
  if (looksLikeStructuredProposalOutput(proposal)) {
    return { body: fallback, acceptedModelOutput: false };
  }
  if (containsInternalImplementationTerm(proposal)) {
    return { body: fallback, acceptedModelOutput: false };
  }
  return { body: proposal, acceptedModelOutput: true };
}

function looksLikeStructuredProposalOutput(text: string): boolean {
  return [
    /^```/,
    /^[-*]\s+/,
    /^#{1,6}\s+/,
    /^[\[{]/,
    /^"[^"]+"\s*:/,
    /\b(requirement|patch|operation|target|className|style)\s*:/i,
  ].some((pattern) => pattern.test(text));
}

function containsInternalImplementationTerm(text: string): boolean {
  return [
    /\bqwen\b/i,
    /\bgemma\b/i,
    /\bwordgard\b/i,
    /\buniver\b/i,
    /\bontology\b/i,
    /\bsemantic\s+patch\b/i,
    /\bcogo\s+json\b/i,
    /\bpreview\b/i,
    /\bdeterministic\s+compiler\b/i,
    /\bfinal\s+writer\b/i,
    /\btool\s*call\b/i,
  ].some((pattern) => pattern.test(text));
}

export async function requestPreviewProposalBody(
  input: PreviewProposalChatInput,
  deps: PreviewProposalChatDeps = {},
): Promise<PreviewProposalChatResult> {
  const fallback = input.userText.trim();
  if (!fallback) {
    return { body: fallback, source: "local-fallback", mode: "requirement" };
  }

  const canUseGateway = deps.canUseGateway ?? canUsePreviewProposalGateway;
  if (!canUseGateway()) {
    return localFallbackProposal(input);
  }

  const service =
    deps.service ?? new GatewayChatService(defaultGatewayConfig());
  const context: ChatContext = {
    ...input.chatContext,
    cogoAppContext: buildCogoAppChatContext(input.currentProject),
    chatModelPolicy: buildStudioChatModelPolicy("preview-proposal"),
  };
  const messages = [
    {
      role: "user",
      content: buildPreviewProposalPrompt(input),
    },
  ];

  let text = "";
  try {
    for await (const chunk of service.sendMessage(messages, context)) {
      if (chunk.type === "text") {
        text += chunk.content;
      } else if (chunk.type === "error") {
        return localFallbackProposal(input);
      }
    }
  } catch {
    return localFallbackProposal(input);
  }
  const normalized = normalizeProposalBody(text, fallback);

  return {
    body: normalized.body,
    source: "qwen-gateway",
    mode:
      normalized.acceptedModelOutput &&
      isTextLikeElementType(input.selectedElementType)
        ? "text-replacement"
        : "requirement",
  };
}
